import React, {useEffect, useRef, useState} from 'react';
import './DemoPanel.css';
import {SCENARIOS, resetAndSeed, getCurrentScenario} from '../../../platform/backends/mock/scenarios';
import {DEMO_PERSONAS, getCurrentPersonaId, PERSONA_STORAGE_KEY} from '../../../platform/backends/mock/fixtures';
import {trackDemoSignupIntent, trackDemoReset, trackDemoSessionStart} from '../../../utils/googleTagManager';
import {useAppState, useAuthService} from '@stores/AppStateContext';
import {DevicePreset} from './devicePresets';
import {DeviceSwitcher} from './DeviceSwitcher';

const SIGN_UP_URL = import.meta.env.VITE_SIGNUP_URL || 'https://app.logycore.com/sign-up';

interface Props {
    // Omitted when this instance is the framed (iframe) view inside a
    // tablet/mobile DeviceFrame — that inner instance already boots at the
    // frame's fixed size, so a device switcher there would just be confusing.
    device?: DevicePreset;
    onDeviceChange?: (device: DevicePreset) => void;
}

export function DemoPanel({device, onDeviceChange}: Props) {
    const appState = useAppState();
    const authService = useAuthService();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [scenario, setScenario] = useState(getCurrentScenario);
    const seededScenario = useRef(getCurrentScenario()).current;
    const [personaId, setPersonaId] = useState(getCurrentPersonaId);

    useEffect(() => {
        trackDemoSessionStart(scenario);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReset = async () => {
        if (busy) return;
        setBusy(true);
        trackDemoReset(scenario);
        await resetAndSeed(scenario);
        setBusy(false);
    };

    const handleScenarioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setScenario(e.target.value);
    };

    const handleSignup = () => {
        trackDemoSignupIntent();
        window.open(SIGN_UP_URL, '_blank', 'noopener');
    };

    const handlePersonaSwitch = async (userId: string) => {
        if (userId === personaId || busy) return;
        setBusy(true);
        try { localStorage.setItem(PERSONA_STORAGE_KEY, userId); } catch {}
        setPersonaId(userId);
        // Save URL before initUserState: the transient state (new userState with no dsManager yet)
        // causes currentUrl to compute a minimal fallback URL and the RouterService reaction
        // pushes it to window.history, losing the current page.
        const savedPath = window.location.pathname + window.location.search + window.location.hash;
        const newUser = await authService.getCurrentUser();
        if (newUser) {
            await appState.initUserState(newUser);
            window.history.replaceState(null, '', savedPath);
            appState.routerService.loadPageBasedOnRequestedUrl();
        }
        setBusy(false);
    };

    const currentPersona = DEMO_PERSONAS.find(p => p.userId === personaId) ?? DEMO_PERSONAS[0];

    return (
        <div className="DemoPanel" role="complementary" aria-label="Demo controls">
            <button
                className="DemoPanel__badge"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                aria-label="Toggle demo controls"
            >
                <span className="DemoPanel__dot" aria-hidden="true" />
                <span>Demo</span>
                <span className="DemoPanel__badge-persona">
                    {currentPersona.label.split(' ')[0]} · {currentPersona.role}
                </span>
                <span className="DemoPanel__chevron" aria-hidden="true">{open ? '▲' : '▼'}</span>
            </button>

            {device && onDeviceChange && (
                <DeviceSwitcher device={device} onChange={onDeviceChange}/>
            )}

            {open && (
                <div className="DemoPanel__drawer">
                    <div className="DemoPanel__section-label">Viewing as</div>
                    <div className="DemoPanel__personas">
                        {DEMO_PERSONAS.map(p => (
                            <button
                                key={p.userId}
                                className={`DemoPanel__persona${personaId === p.userId ? ' DemoPanel__persona--active' : ''}`}
                                onClick={() => handlePersonaSwitch(p.userId)}
                                disabled={busy}
                                title={`${p.label} — ${p.role}`}
                            >
                                <span className="DemoPanel__persona-name">{p.label}</span>
                                <span className="DemoPanel__persona-role">{p.role}</span>
                            </button>
                        ))}
                    </div>

                    {SCENARIOS.length > 1 && (
                        <>
                            <div className="DemoPanel__section-label">Scenario</div>
                            <select
                                className="DemoPanel__select DemoPanel__select--full"
                                value={scenario}
                                onChange={handleScenarioChange}
                                aria-label="Select scenario"
                            >
                                {SCENARIOS.map(s => (
                                    <option key={s.name} value={s.name}>{s.label}</option>
                                ))}
                            </select>
                        </>
                    )}
                    <div className="DemoPanel__actions">
                        <button
                            className="DemoPanel__btn DemoPanel__btn--ghost"
                            onClick={handleReset}
                            disabled={busy}
                            aria-label={scenario !== seededScenario ? 'Switch to selected scenario' : 'Reset demo data'}
                        >
                            {busy ? 'Loading…' : scenario !== seededScenario ? 'Switch' : 'Reset demo'}
                        </button>
                        <button
                            className="DemoPanel__btn DemoPanel__btn--primary"
                            onClick={handleSignup}
                            aria-label="Sign up for Logycore"
                        >
                            Sign up free
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
